import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowRight, ArrowUpRight, ChevronRight, ChevronDown, CheckCircle2, Layers3, Rocket, ShieldCheck, ExternalLink } from 'lucide-react';
import { companyInfo, projects, services, techStack, registrations, clients, directors } from '../data/mock';
import { ParticlesBg } from '../components/layout/ParticlesBg';
import { Section, SectionHeading, Eyebrow, Ridgeline, fadeInUp } from '../components/layout/Section';
import { SpotlightCard, Marquee, CountUp, WindowFrame, StatusPill, LogoTile, Reveal } from '../components/layout/Tech';
import { SplitProfile } from '../components/layout/SplitProfile';
import { ChartCard, Donut, BarsH } from '../components/layout/Charts';
import { Crown } from 'lucide-react';

const serviceAccents = ['accent-purple', 'accent-cyan', 'accent-orange', 'accent-pink', 'accent-green', 'accent-blue'];

const allTools = [...techStack.frontend, ...techStack.backend, ...techStack.dataInfra, ...techStack.contentSeo, ...techStack.aiRag, ...techStack.documentOcr];
const marqueeTools = allTools.filter((t) => t.logo).filter((t, i, arr) => arr.findIndex((x) => x.logo === t.logo) === i);

const liveCount = projects.filter((p) => p.status === 'Live').length;
const byStatus = Object.entries(projects.reduce((m, p) => ({ ...m, [p.status]: (m[p.status] || 0) + 1 }), {})).map(([name, value]) => ({ name, value }));
const byCountry = Object.entries(clients.reduce((m, c) => { const k = c.location.split(', ').pop(); return { ...m, [k]: (m[k] || 0) + 1 }; }, {})).map(([name, value]) => ({ name, value })).sort((x, y) => y.value - x.value);
const byBranch = [
  { name: 'Frontend', value: techStack.frontend.length },
  { name: 'Data / Infra', value: techStack.dataInfra.length },
  { name: 'Content / SEO', value: techStack.contentSeo.length },
  { name: 'AI / RAG', value: techStack.aiRag.length },
  { name: 'Backend', value: techStack.backend.length },
  { name: 'Document / OCR', value: techStack.documentOcr.length }
];

const ProjectMark = ({ project, size = 44 }) => {
  const Icon = project.icon;
  if (project.logo) {
    return (
      <span className="brand-mark" style={{ width: size, height: size, background: project.logoBg || '#fff' }}>
        <img src={project.logo} alt={`${project.title} logo`} />
      </span>
    );
  }
  return <span className="icon-well" style={{ width: size, height: size }}><Icon className="w-5 h-5" /></span>;
};

const HeroReadout = () => (
  <WindowFrame url="globistaan.com / company.json" right={<StatusPill status="Live" />} className="w-full">
    <div className="readout">
      <span className="c">// {companyInfo.tagline}</span>{'\n'}
      <span className="k">company</span>: <span className="s">"{companyInfo.fullName}"</span>{'\n'}
      <span className="k">founded</span>: <span className="n">{companyInfo.founded}</span> <span className="c">// incorporated {companyInfo.incorporationDate}</span>{'\n'}
      <span className="k">base</span>: <span className="s">"Baijnath, Kangra, Himachal Pradesh"</span>{'\n'}
      <span className="k">products</span>: <span className="n">{projects.length}</span> <span className="c">// {liveCount} live</span>{'\n'}
      <span className="k">clients</span>: <span className="n">{clients.length}</span>{'\n'}
      <span className="k">registrations</span>: [<span className="s">"MCA"</span>, <span className="s">"GST"</span>, <span className="s">"MSME"</span>, <span className="s">"DGFT"</span>, <span className="s">"DPIIT"</span>]{'\n'}
      <span className="k">focus</span>: [<span className="s">"AI-enabled apps"</span>, <span className="s">"SaaS platforms"</span>, <span className="s">"automation"</span>]{'\n'}
      <span className="ok">✓ ready</span> <span className="cursor-blink" />
    </div>
  </WindowFrame>
);

const HeroSection = () => (
  <Section
    tone="ink"
    pattern="grid"
    aurora
    topo
    watermark
    rule={false}
    padding="pt-36 pb-32"
    className="min-h-screen flex items-center"
    contentWidth={1280}
    glows={[
      { color: 'rgba(34,211,238,0.26)', top: '-12%', left: '-8%', size: 620 },
      { color: 'rgba(124,58,237,0.3)', top: '20%', right: '-12%', size: 620 },
      { color: 'rgba(52,211,153,0.16)', bottom: '-10%', left: '35%', size: 480 }
    ]}
  >
    <ParticlesBg />
    <div className="noise" aria-hidden="true" />
    <div className="scanline" aria-hidden="true" />

    <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center">
      <div>
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mb-7">
          <Eyebrow>AI Innovation from the Himalayas</Eyebrow>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="h-display mb-7"
          style={{ fontSize: 'clamp(2.8rem, 6.4vw, 5.4rem)' }}
        >
          <span>Where </span>
          <span className="gradient-text">Mountains</span>
          <br />
          <span>Meet </span>
          <span className="gradient-text">Machines</span>
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }} className="lede mb-6" style={{ maxWidth: 600 }}>
          {companyInfo.subtitle}
        </motion.p>

        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.42 }} className="mb-10 chip" style={{ display: 'inline-flex' }}>
          <span className="inline-block w-1.5 h-1.5 rounded-full blink" style={{ background: '#34D399', boxShadow: '0 0 8px #34D399' }} />
          Date of Incorporation: {companyInfo.incorporationDate}
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }} className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Link to="/projects" className="btn-primary btn-accent" style={{ padding: '1rem 2.2rem', fontSize: '0.85rem' }}>
            Explore Our Projects <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/investors" className="btn-secondary" style={{ padding: '1rem 2.2rem', fontSize: '0.85rem' }}>
            Investor Deck <ArrowUpRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0, x: 30, rotateY: -6 }} animate={{ opacity: 1, x: 0, rotateY: 0 }} transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }} style={{ perspective: 1200 }}>
        <HeroReadout />
      </motion.div>
    </div>

    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 0.8 }} className="relative z-10 mt-16 scroll-indicator text-center">
      <ChevronDown className="w-6 h-6 mx-auto" style={{ color: 'rgba(255,255,255,0.5)' }} />
    </motion.div>

    <Ridgeline height={200} />
  </Section>
);

const StackMarquee = () => (
  <Section tone="ink" rule={false} padding="py-10" contentWidth={1400} className="border-t border-white/5">
    <div className="flex flex-col md:flex-row items-center gap-5 md:gap-10">
      <p className="kicker flex-shrink-0" style={{ color: 'rgba(255,255,255,0.5)' }}>Built with</p>
      <Marquee speed={55} className="flex-1 w-full">
        {marqueeTools.map((t) => <LogoTile key={t.name} name={t.name} logo={t.logo} description={t.description} />)}
      </Marquee>
    </div>
  </Section>
);

const StatStrip = () => (
  <Section tone="light" pattern="none" padding="py-14" contentWidth={1200}>
    <Reveal>
      <div className="stat-strip" style={{ '--stat-cols': 4 }}>
        {[
          { value: String(projects.length), label: 'Products & client builds', accent: 'var(--cyan-deep)' },
          { value: String(clients.length), label: 'Clients across 3 countries', accent: 'var(--violet-deep)' },
          { value: String(registrations.length), label: 'Government registrations', accent: 'var(--emerald-deep)' },
          { value: companyInfo.founderExperience, label: 'Founder experience in technology', accent: 'var(--amber-deep)' }
        ].map((s) => (
          <div key={s.label} className="stat-cell">
            <CountUp value={s.value} style={{ fontSize: '2.4rem', color: s.accent }} />
            <span className="kicker">{s.label}</span>
          </div>
        ))}
      </div>
    </Reveal>
  </Section>
);

const ServicesSection = () => (
  <Section tone="light" pattern="dots" glows={[{ color: 'rgba(34,211,238,0.35)', top: '-20%', right: '-8%', size: 520 }]} padding="pt-6 pb-24">
    <SectionHeading
      eyebrow="What We Do"
      title="AI Services & Solutions"
      subtitle="From AI-enabled business apps to SaaS platforms, we deliver practical technology solutions for real workflows."
      className="mb-14"
    />

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {services.map((service, i) => {
        const Icon = service.icon;
        return (
          <Reveal key={service.title} delay={i * 0.07} className="h-full">
            <SpotlightCard accent={serviceAccents[i % serviceAccents.length]} className="h-full" style={{ '--card-pad': '2rem' }}>
              <div className="flex items-start justify-between mb-6">
                <div className="icon-well"><Icon className="w-6 h-6" /></div>
                <ArrowUpRight className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
              </div>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: '0.6rem', color: 'var(--text-primary)' }}>{service.title}</h3>
              <p style={{ fontSize: '0.92rem', lineHeight: 1.6, color: 'var(--text-secondary)' }}>{service.description}</p>
            </SpotlightCard>
          </Reveal>
        );
      })}
    </div>
  </Section>
);

const WhyUsSection = () => {
  const reasons = [
    { icon: Layers3, accent: 'accent-green', title: 'Built Beyond the First Launch', text: 'Anyone can spin up software now. We design systems for the harder part: keeping them dependable as real customers, workflows, and priorities keep changing.' },
    { icon: Rocket, accent: 'accent-cyan', title: 'A Platform That Keeps Morphing', text: 'Think of Globistaan as a managed platform that grows with your business, so your software can adapt as quickly as your next opportunity.' },
    { icon: ShieldCheck, accent: 'accent-orange', title: 'Lean, Managed Scale', text: 'We help you stay ahead of the curve without carrying heavy technical overhead, keeping execution focused, flexible, and competitively priced.' }
  ];

  return (
    <Section
      tone="teal"
      pattern="grid"
      topo
      glows={[
        { color: 'rgba(52,211,153,0.22)', top: '-10%', left: '-6%', size: 560 },
        { color: 'rgba(34,211,238,0.18)', bottom: '-20%', right: '-8%', size: 560 }
      ]}
      contentWidth={1180}
    >
      <div className="noise" aria-hidden="true" />
      <motion.div {...fadeInUp} className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-14 items-start">
        <div>
          <div className="mb-5"><Eyebrow style={{ '--eyebrow-fg': '#6EE7B7', '--eyebrow-border': 'rgba(52,211,153,0.35)', '--eyebrow-bg': 'rgba(52,211,153,0.1)' }}>Why Us</Eyebrow></div>
          <h2 className="h-section" style={{ fontSize: 'clamp(1.9rem, 4.2vw, 3.2rem)', maxWidth: 620 }}>
            Built for the era where software is easy to start, but hard to sustain.
          </h2>
        </div>
        <div>
          <p className="lede" style={{ marginBottom: '1.25rem', maxWidth: 'none' }}>
            We're living in a wild new era. With AI and agentic tech, anyone can spin up software. But here's the truth: building something is easy, while maintaining and scaling it as your business changes is where things usually break down.
          </p>
          <p className="lede" style={{ maxWidth: 'none' }}>
            We built our company to solve exactly that. Globistaan works like a managed platform that morphs and grows as your business accelerates, giving you the power to stay ahead of the curve while keeping overhead incredibly lean and competitively priced.
          </p>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-14">
        {reasons.map((reason, i) => {
          const Icon = reason.icon;
          return (
            <Reveal key={reason.title} delay={i * 0.08} className="h-full">
              <SpotlightCard accent={reason.accent} className="h-full" style={{ '--card-pad': '1.75rem' }}>
                <div className="icon-well sm mb-5"><Icon className="w-5 h-5" /></div>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '0.7rem', color: 'var(--text-primary)' }}>{reason.title}</h3>
                <p style={{ fontSize: '0.92rem', lineHeight: 1.62, color: 'var(--text-secondary)' }}>{reason.text}</p>
              </SpotlightCard>
            </Reveal>
          );
        })}
      </div>
    </Section>
  );
};

const NumbersSection = () => (
  <Section tone="slate" pattern="grid" topo padding="py-20">
    <SectionHeading
      eyebrow="By the numbers"
      title="What we build, where it runs"
      subtitle="Derived from the projects, clients and tooling listed on this site, so the picture stays honest as the portfolio grows."
      className="mb-12"
    />
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
      <Reveal className="h-full"><ChartCard title="Portfolio by status" caption="Products and client builds right now." data={byStatus}><Donut data={byStatus} centerLabel="projects" size={170} /></ChartCard></Reveal>
      <Reveal delay={0.06} className="h-full"><ChartCard title="Clients by country" caption="Where the people we build for are based." data={byCountry} valueLabel="Clients"><BarsH data={byCountry} /></ChartCard></Reveal>
      <Reveal delay={0.12} className="h-full"><ChartCard title="Tools by stack branch" caption="Breadth of the engineering toolkit." data={byBranch} valueLabel="Tools"><BarsH data={byBranch} /></ChartCard></Reveal>
    </div>
  </Section>
);

const DirectorsSection = () => (
  <>
    <Section id="directors" tone="ink" pattern="grid" topo padding="py-20" contentWidth={1400} className="scroll-mt-16">
      <SectionHeading
        eyebrow="Leadership"
        title="Directors"
        subtitle="Visionary leaders with deep industry experience steering Globistaan's AI journey."
        className="mb-0"
        maxWidth={520}
      />
    </Section>
    <Section tone="navy" pattern="none" aurora padding="py-0" contentWidth={1920} rule={false} containerClassName="!px-0 md:!px-0">
      <div className="split-row">
        {directors.map((member, i) => (
          <SplitProfile
            key={member.name}
            compact
            index={`Director · ${String(i + 1).padStart(2, '0')}`}
            image={member.avatar}
            imagePosition={member.avatarPosition}
            name={member.name}
            role={member.role}
            bio={member.bio}
            caption="Board of Directors"
            captionIcon={Crown}
            accent={i % 2 === 0 ? 'cyan' : 'violet'}
          />
        ))}
      </div>
    </Section>
  </>
);

const ProjectsPreview = () => (
  <Section
    tone="indigo"
    pattern="dots"
    aurora
    glows={[
      { color: 'rgba(124,58,237,0.32)', top: '-10%', right: '-6%', size: 600 },
      { color: 'rgba(34,211,238,0.18)', bottom: '-10%', left: '-8%', size: 520 }
    ]}
    contentWidth={1280}
  >
    <motion.div {...fadeInUp} className="flex flex-col md:flex-row items-start md:items-end justify-between mb-10 gap-6">
      <div>
        <div className="mb-4"><Eyebrow style={{ '--eyebrow-fg': '#C4B5FD', '--eyebrow-border': 'rgba(167,139,250,0.4)', '--eyebrow-bg': 'rgba(167,139,250,0.12)' }}>Current Projects</Eyebrow></div>
        <h2 className="h-section" style={{ fontSize: 'clamp(1.7rem, 3.6vw, 2.5rem)' }}>What We're Building</h2>
      </div>
      <Link to="/projects" className="btn-secondary text-xs" style={{ textDecoration: 'none' }}>
        View All Projects <ChevronRight className="w-3.5 h-3.5" />
      </Link>
    </motion.div>

    <div className="rail">
      {projects.map((project, i) => (
        <Reveal key={project.id} delay={Math.min(i, 3) * 0.06} className="h-full">
          <Link to="/projects" className="no-underline block h-full">
            <SpotlightCard accent={project.accentClass} className="h-full" style={{ '--card-pad': '0' }}>
              <div className="win-bar" style={{ borderBottomColor: 'rgba(255,255,255,0.08)' }}>
                <span className="win-dots"><i /><i /><i /></span>
                <span className="win-url">{project.website ? project.website.replace(/^https?:\/\//, '').replace(/\/$/, '') : project.category.toLowerCase().replace(/\s+/g, '-')}</span>
                <span className="win-right"><StatusPill status={project.status} /></span>
              </div>
              <div style={{ padding: '1.75rem' }}>
                <div className="flex items-center gap-3 mb-5">
                  <ProjectMark project={project} />
                  <span className="chip" style={{ color: 'var(--card-accent)', borderColor: 'var(--card-accent)' }}>{project.category}</span>
                </div>
                <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: '0.6rem', color: 'var(--text-primary)' }}>{project.title}</h3>
                <p className="clamp-4" style={{ fontSize: '0.9rem', lineHeight: 1.6, color: 'var(--text-secondary)', marginBottom: '1.25rem' }}>{project.description}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {project.features.slice(0, 3).map((f) => (
                    <span key={f} className="feature-line"><CheckCircle2 className="w-3 h-3" /> {f}</span>
                  ))}
                </div>
              </div>
            </SpotlightCard>
          </Link>
        </Reveal>
      ))}
    </div>
  </Section>
);

const ClientsStrip = () => (
  <Section tone="slate" pattern="none" padding="py-14" contentWidth={1200}>
    <Reveal>
      <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
        <p className="kicker flex-shrink-0">Trusted by</p>
        <div className="flex flex-wrap items-center justify-center gap-3 flex-1">
          {clients.map((c) => (
            <a key={c.name} href={c.website} target="_blank" rel="noreferrer" className="logo-tile logo-tile-lg no-underline">
              <span className="logo-tile-mark" style={{ background: c.logo === '/images/projects/dailogue.png' ? '#070A12' : '#fff' }}><img src={c.logo || c.image} alt={`${c.name} logo`} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 3 }} /></span>
              <span className="logo-tile-name">{c.name}</span>
              <ExternalLink className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
            </a>
          ))}
        </div>
      </div>
    </Reveal>
  </Section>
);

export default function Home() {
  return (
    <main>
      <HeroSection />
      <StackMarquee />
      <StatStrip />
      <ServicesSection />
      <WhyUsSection />
      <DirectorsSection />
      <ProjectsPreview />
      <NumbersSection />
      <ClientsStrip />
    </main>
  );
}
